import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../components/Icon";
import UserCell from "../../components/UserCell";
import moment from 'moment';
import PanelTemplate from "../../templates/Panel";
import Button from "../../components/Button";
import ButtonDropdown from "../../components/ButtonDropdown";
import Select from "react-select";
import { useDrag, useDrop, useDragLayer, DndProvider } from "react-dnd";
import { HTML5Backend, getEmptyImage } from "react-dnd-html5-backend";
import { convertIntToMoney, convertMoenyToInt } from "../../helpers/conversions";
import Modal from "../../components/Modal";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { createOptions } from "../../helpers/selects";
import Input from "../../components/Input";
import { PointerSensor } from "@dnd-kit/core";
import { toast } from "react-toastify";
import SelectCustomerCompany from "../../components/selects/SelectCustomerCompany";
import { ModalCreateCompany } from "../../components/ModalCreateCompany";
import { ModalCreateCustomer } from "../../components/ModalCreateCustomer";
import SelectCustomer from "../../components/selects/SelectCustomer";
import SelectBudget from "../../components/selects/SelectBudget";
import SelectOrder from "../../components/selects/SelectOrder";
import InputSelect from "../../components/InputSelect";
import TagChip from "../../components/TagChip";
import { convertToMoney } from "../../helpers/masks";
import InputDate from "../../components/InputDate";
import { Link } from "react-router-dom";
import temperatureOptions from "../../seeds/crm/temperatures";
import statusOptions from "../../seeds/crm/status";
import segmentsOptions from "../../seeds/segments";
import states from "../../seeds/states";
import SelectCompany from "../../components/selects/SelectCompany";
import SelectAdmin from "../../components/selects/SelectAdmin";
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import Box from "../../components/Box";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";
import { getUser, hasPermission, hasPlanFeature } from "../../services/auth";
import PipelineMembersAvatars from "../../components/PipelineMembersAvatars";
import PipelineMembersModal from "./Pipelines/PipelineMembersModal";

const orderByOptions = [
    {label: 'Mais tempo parada na etapa', value: 'entered_stage_at,asc'},
    {label: 'Menos tempo parada na etapa', value: 'entered_stage_at,desc'},
    {label: 'Maior valor', value: 'amount,desc'}, 
    {label: 'Menor valor', value: 'amount,asc'},  
    {label: 'Previsão de conclusão mais longe', value: 'closing_date,desc'},
    {label: 'Previsão de conclusão mais perto', value: 'closing_date,asc'},
    {label: 'Data da criação do card mais recente', value: 'created_at,desc'},
    {label: 'Data da criação do card mais antiga', value: 'created_at,asc'}
];

const hasActiveFilterValue = value => value !== null && value !== undefined && value !== '';

const countActiveCrmFilters = searchParams => {
    const params = Object.fromEntries(searchParams.entries());

    return [
        params.activity_status,
        params.company_id,
        params.customer_id,
        params.admin_id,
        params.budget_code,
        searchParams.getAll('crm_tag_ids').length > 0 ? 'crm_tag_ids' : null,
        params.status && params.status !== 'open',
        params.created_at_start || params.created_at_final,
        params.closing_date_start || params.closing_date_final,
        params.amount_start || params.amount_final,
        params.order_by && params.order_by !== 'created_at,desc',
    ].filter(hasActiveFilterValue).length;
};

const knownFilterKeys = [
    'activity_status', 'company_id', 'customer_id', 'admin_id', 'budget_code',
    'status', 'created_at_start', 'created_at_final', 'closing_date_start', 'closing_date_final',
    'amount_start', 'amount_final', 'order_by'
];

// Reconstrói os params de filtro do backend a partir da URL. A URL é escrita
// por buildFilterParams (via setSearchParams), então os valores já chegam no
// formato que o backend espera — basta reaproveitá-los no fetch inicial.
const buildParamsFromSearchParams = searchParams => {
    const params = {};
    knownFilterKeys.forEach(key => {
        const value = searchParams.get(key);
        if (value != null && value !== '') {
            params[key] = value;
        }
    });
    const tagIds = searchParams.getAll('crm_tag_ids');
    if (tagIds.length > 0) {
        params.crm_tag_ids = tagIds;
    }
    return params;
};

const boardStateKey = pipelineId => `crm_board_state_${pipelineId}`;

// Origem/vínculo do card criado manualmente: avulso ou ligado a um orçamento
// ou pedido existente (o backend preenche valor, empresa, pessoa e responsável
// a partir do registro vinculado).
const cardLinkTypeOptions = [
    {label: 'Card avulso', value: 'standalone'},
    {label: 'Vinculado a um orçamento', value: 'budget'},
    {label: 'Vinculado a um pedido', value: 'order'},
];

const originOptions = [
    {label: 'Todas', value: ''},
    {label: 'Site', value: 'site'},
    {label: 'Sistema', value: 'sistema'}
];

const emailStatusOptions = [
    {label: 'Todos', value: ''},
    {label: 'Somente lidos', value: 'read'},
    {label: 'Não lidos', value: 'unread'}
];

const activityStatusOptions = [
    {label: 'Todas', value: ''},
    {label: 'Com atividades em aberto', value: 'open'}
];

// O seletor de pipeline é renderizado como o título da página: sem moldura,
// só o nome em destaque com o caret ao lado.
const pipelineSwitchStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '30px',
        height: '30px',
        border: 'none',
        borderRadius: '3px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(61, 89, 128, 0.18)' : 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer'
    }),
    valueContainer: provided => ({
        ...provided,
        padding: 0
    }),
    singleValue: provided => ({
        ...provided,
        margin: 0,
        color: '#1A1A1A',
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: 1.2
    }),
    placeholder: provided => ({
        ...provided,
        margin: 0,
        color: '#696969',
        fontSize: '20px',
        fontWeight: 700
    }),
    input: provided => ({
        ...provided,
        margin: 0,
        padding: 0,
        fontSize: '20px',
        fontWeight: 700
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: provided => ({
        ...provided,
        padding: '0 0 0 4px',
        color: '#3D5980'
    }),
    menu: provided => ({
        ...provided,
        zIndex: 50,
        minWidth: '260px',
        borderRadius: '3px',
        overflow: 'hidden',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)'
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '13px',
        fontWeight: state.isSelected ? 700 : 400,
        color: state.isSelected ? '#fff' : '#1A1A1A',
        backgroundColor: state.isSelected ? '#3D5980' : state.isFocused ? '#EDF2F8' : '#fff',
        cursor: 'pointer'
    })
};

// Acento de cada etapa (índice da coluna → cor). Cicla quando a pipeline tem
// mais etapas que cores.
const STAGE_ACCENTS = ['#3D5980', '#2066B0', '#17A398', '#C17F3E', '#6C4CE0', '#E31C58', '#1B9E77', '#44628A'];

const stageAccent = index => STAGE_ACCENTS[index % STAGE_ACCENTS.length];

const formatDaysInStage = days => `${days} dia${days !== 1 ? 's' : ''} aqui`;

const formatStageValue = value => convertIntToMoney(
    Number.isInteger(value) ? value : Math.round(Number(value) * 100),
    true
);

   const taskTypeLabel = {
    call: "realizar ligação",
    meeting: "realizar reunião",
    email: "enviar email",
    task: "realizar tarefa"
    };

const statusLabel = statusOptions.reduce((acc, status) => {
    acc[status.value] = status.label;
    return acc;
}, {});

// Ganho/Perdido só existem em pipelines "com resultado comercial". Em pipelines
// operacionais (card_status_mode = operational) o card apenas fica aberto ou
// arquivado — a regra é da pipeline e vale para todos os seus cards,
// independentemente da origem.
const commercialOnlyStatuses = new Set(['won', 'lost']);

const pipelineAllowsCommercialResult = pipeline => pipeline?.card_status_mode !== 'operational';

const getStatusOptionsForPipeline = pipeline => (
    pipelineAllowsCommercialResult(pipeline)
        ? statusOptions
        : statusOptions.filter(status => !commercialOnlyStatuses.has(status.value))
);

const taskActivityLabel = (card) => {
    const t = card?.next_pending_task;
    if (!t || t.status !== 'pending' || !t.date || taskTypeLabel[t.type] == null) {
        return '—';
    }
    const days = moment(t.date).startOf('day').diff(moment().startOf('day'), 'days');
    if (days < 0) {
        return `Atrasado ${Math.abs(days)} dia${Math.abs(days) !== 1 ? 's' : ''} para ${taskTypeLabel[t.type]}`;
    }
    if (days === 0) {
        return `Hoje: ${taskTypeLabel[t.type]}`;
    }
    return `${days} dia${days !== 1 ? 's' : ''} para ${taskTypeLabel[t.type]}`;
};

// Mesma regra do backend (PipelineService): Administrador e Suporte sempre
// enxergam todos os cards, mesmo com a permissão "view_related_cards_crm".
const isRestrictedCardView = () =>
    hasPermission('view_related_cards_crm') &&
    !['Administrador', 'Suporte'].includes(getUser()?.role?.name);

const CardTags = ({ tags }) => {
    if (!tags || tags.length === 0) return null;
    return (
        <div className="card-tags">
            {tags.map(tag => (
                <TagChip key={tag.id} name={tag.name} color={tag.color} className="card-tag" />
            ))}
        </div>
    );
};


// Detalhes do produto exibidos na visualização em lista: imagem + nome do
// produto em destaque, com código e quantidade numa linha secundária compacta.
const CardListProduct = ({ orderItem, code }) => (
    <div className="crm-list-product">
        <img
            className="crm-list-product__img"
            src={orderItem.image_url?.small}
            alt={orderItem.product_name}
            onError={(e) => { e.target.src = require('../../assets/images/svgs/default.svg').default; }}
        />
        <div className="crm-list-product__info">
            <span className="crm-list-product__name" title={orderItem.subproduct_name || orderItem.product_name}>
                {orderItem.subproduct_name || orderItem.product_name}
            </span>
            <span className="crm-list-product__meta">
                {(code != null && code !== '') && <span className="crm-list-product__code">#{code}</span>}
                <span className="crm-list-product__qty">{`Qtd: ${orderItem.quantity}`}</span>
            </span>
        </div>
    </div>
);

// Conteúdo visual interno do card (reutilizado pelo card real e pelo preview de drag)
const CardBody = ({ card }) => {
    const t = card.next_pending_task;
    const nextTask = (t && t.status === 'pending' && t.date) ? t : null;
    const callDays = nextTask?.date
        ? moment(nextTask.date).startOf('day').diff(moment().startOf('day'), 'days')
        : null;
    const activityText = nextTask && taskTypeLabel[nextTask.type] != null
        ? callDays < 0
            ? `Atrasado ${Math.abs(callDays)} dia${Math.abs(callDays) !== 1 ? 's' : ''} para ${taskTypeLabel[nextTask.type]}`
            : callDays === 0
                ? `Hoje: ${taskTypeLabel[nextTask.type]}`
                : `${callDays} dia${callDays !== 1 ? 's' : ''} para ${taskTypeLabel[nextTask.type]}`
        : null;
    const productName = card.order_item?.subproduct_name || card.order_item?.product_name;
    const title = card.company?.trade_name || card.customer?.name || productName || `Card #${card.id}`;
    const subtitle = card.company?.trade_name && card.customer?.name ? card.customer.name : null;
    const location = card?.company?.state && card?.company?.city
        ? `${card.company.city} / ${card.company.state}`
        : null;

    return (
        <>
            <div className="card-top">
                {card.code &&
                    <span className="card-code">#{card.code}</span>
                }
                <span className="card-days">{formatDaysInStage(card.days_in_stage)}</span>
            </div>
            <div className="card-title">{title}</div>
            {(subtitle || location) &&
                <div className="card-meta">
                    {subtitle &&
                        <span title={subtitle}>{subtitle}</span>
                    }
                    {location &&
                        <span title={location}>
                            <Icon name="card-location" size={11} />
                            {location}
                        </span>
                    }
                </div>
            }
            {(card.label || (card.crm_tags ?? []).length > 0) &&
                <div className="card-badges">
                    {card.label &&
                        <div className="card-label" style={{ backgroundColor: card.label.background}}>
                            <span style={{ color: card.label.color}}>{card.label.name}</span>
                        </div>
                    }
                    <CardTags tags={card.crm_tags} />
                </div>
            }
            {card.order_item &&
                <div className="card-product">
                    <img
                        src={card.order_item.image_url?.small}
                        alt={card.order_item.product_name}
                        onError={(e) => { e.target.src = require('../../assets/images/svgs/default.svg').default; }}
                    />
                    <div className="card-product-info">
                        <span className="name" title={productName}>{productName}</span>
                        <span className="qty">{`${card.order_item.quantity} un.`}</span>
                    </div>
                </div>
            }
            <div className="card-bottom">
                <strong className="card-amount">{convertIntToMoney(card.amount, true)}</strong>
                <span className="card-owner" title={card?.admin ? `Responsável: ${card.admin.name}` : 'Sem responsável'}>
                    {card?.admin
                        ? <UserCell name={card.admin.name} photo={card.admin.photo_url} size={20} initialFontSize={8} maxInitials={2} />
                        : <span className="card-owner--empty"><Icon name="card-owner" size={14} />Sem responsável</span>
                    }
                </span>
            </div>
            {activityText &&
                <div className={`card-activity${callDays < 0 ? ' is-late' : callDays === 0 ? ' is-today' : ''}`}>
                    <Icon name="timer" size={12} />
                    <span>{activityText}</span>
                </div>
            }
        </>
    );
};

const Card = ({ card, stage, pipeline, moveCard, findCard, updateCard, onCardClick }) => {
    const [status, setStatus] = useState(card.status);
    const nodeRef = useRef(null);
    const availableStatusOptions = getStatusOptionsForPipeline(pipeline);

    useEffect(() => {
        setStatus(card.status);
    }, [card.status]);

    const originalIndex = findCard(stage, card.id).index;

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: 'card',
        item: () => ({
            id: card.id,
            originalIndex,
            stageId: stage.id,
            card,
            width: nodeRef.current?.offsetWidth
        }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    // Esconde a imagem nativa de drag (que ignora o border-radius e mostra um retângulo).
    // O visual durante o arraste é renderizado por <CardDragLayer />.
    useEffect(() => {
        dragPreview(getEmptyImage(), { captureDraggingState: true });
    }, [dragPreview]);

    const setRefs = (node) => {
        nodeRef.current = node;
        drag(node);
    };

    const opacity = isDragging ? 0 : 1;

    const updateCardParam = async (param, value) => {
        updateCard({
            ...card,
            [param]: value
        });
    }

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick();
        }
    }

    return (
        <div ref={setRefs} style={{ opacity }} className={`card ${card.color}${card?.updating === true ? ' updating' : ''}`}>
            <Link to={`/crm/card/${card.id}`} state={{ from: 'crm-board' }} onClick={handleCardClick}>
                <CardBody card={card} />
            </Link>
            <select
                value={status}
                onClick={event => event.stopPropagation()}
                onChange={event => {
                    let value = event.target.value;
                    if (!pipelineAllowsCommercialResult(pipeline) && commercialOnlyStatuses.has(value)) return;

                    setStatus(value)
                    updateCardParam('status', value);
                }}
            >
                {availableStatusOptions.map((status, index) => (
                    <option key={index} value={status.value}>{status.label}</option>
                ))}
            </select>
        </div>

    );
};

// Preview customizado durante o drag: card arredondado seguindo o cursor (sem o retângulo nativo)
const CardDragLayer = () => {
    const { isDragging, item, itemType, currentOffset } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        currentOffset: monitor.getSourceClientOffset(),
        isDragging: monitor.isDragging()
    }));

    if (!isDragging || itemType !== 'card' || !currentOffset || !item?.card) {
        return null;
    }

    const card = item.card;

    return (
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1000 }}>
            <div
                className={`card ${card.color} card-drag-preview`}
                style={{
                    transform: `translate(${currentOffset.x}px, ${currentOffset.y}px) rotate(2deg)`,
                    width: item.width || 220,
                    margin: 0
                }}
            >
                <CardBody card={card} />
            </div>
        </div>
    );
};

const Column = ({ index, totals, stage, pipeline, boardValue, moveCard, findCard, updateCard, onLoadMore, onCardClick }) => {
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [{ isOver }, drop] = useDrop({
        accept: 'card',
        drop: (item) => {
            moveCard(item.id, item.stageId, stage.id);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    });

    const handleScroll = async (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        const pagination = stage.cards_pagination;

        if (
            nearBottom &&
            !isLoadingMore &&
            pagination &&
            pagination.current_page < pagination.last_page
        ) {
            setIsLoadingMore(true);

            try {
                if (typeof onLoadMore === 'function') {
                    await onLoadMore(stage.id);
                }
            } finally {
                setIsLoadingMore(false);
            }
        }
    };

    const stageValue = stage.cards_pagination?.total_value;
    const share = stageValue != null && Number(boardValue) > 0
        ? Math.round((Number(stageValue) / Number(boardValue)) * 100)
        : 0;

    return (
        <div ref={drop} className={`column ${isOver ? 'hovered' : ''}`} style={{ '--stage-accent': stageAccent(index) }}>
            <div className="column-title">
                <div className="column-title__row">
                    <span className="column-index">{String(index + 1).padStart(2, '0')}</span>
                    <h2 title={stage.name}>{stage.name}</h2>
                    <span className="column-count">{stage.cards_pagination?.total ?? totals.totalStages[index]?.totalCards ?? 0}</span>
                </div>
                <div className="column-title__row column-title__row--meta">
                    <span className="column-value">{stageValue != null ? formatStageValue(stageValue) : '—'}</span>
                    <span className="column-share" title="Participação no valor total da pipeline">{share}%</span>
                </div>
                <span className="column-bar" aria-hidden="true">
                    <span style={{ width: `${share}%` }} />
                </span>
            </div>
            <div
                className="column-cards"
                onScroll={handleScroll}
            >
                {stage.cards.length === 0 && !isLoadingMore &&
                    <div className="column-empty">
                        <Icon name="inbox" size={20} />
                        <span>Nenhum card nesta etapa</span>
                    </div>
                }
                {stage.cards.map(card => (
                    <Card
                        key={card.id}
                        card={card}
                        stage={stage}
                        pipeline={pipeline}
                        moveCard={moveCard}
                        findCard={findCard}
                        updateCard={updateCard}
                        onCardClick={onCardClick}
                    />
                ))}
                {isLoadingMore && (
                    <div className="column-loading-more">Carregando mais cards...</div>
                )}
            </div>
        </div>
    );
};

export const CRMList = () => {


    const [option, setOption] = useState('');
    const [gettingPipelines, setGettingPipelines] = useState(true);

    const [pipelines, setPipelines] = useState([]);
    const [pipeline, setPipeline] = useState(undefined);


    const [gettingStages, setGettingStages] = useState(false);
    const [stages, setStages] = useState([]);

    const [cardToCreate, setCardToCreate] = useState(null);
    const [showCreateCompany, setShowCreateCompany] = useState(false);
    const [showCreateCustomer, setShowCreateCustomer] = useState(false);
    

    const [creatingCard, setCreatingCard] = useState(false);

    const [editFilter, setEditFilter] = useState(false);

    // Quantidade de cards carregados por página em cada etapa
    const [cardsPerPage] = useState(20);

    // Modo de visualização: 'kanban' (padrão) ou 'list'
    const [viewMode, setViewMode] = useState(() => {
        const saved = localStorage.getItem('crm_view_mode');
        return saved === 'list' ? 'list' : 'kanban';
    });

    // Estado da visualização em lista (paginação plana sobre toda a pipeline)
    const [cards, setCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [cardsPage, setCardsPage] = useState(1);
    const [cardsLastPage, setCardsLastPage] = useState(1);
    const [cardsTotal, setCardsTotal] = useState(0);
    const [cardsHasNext, setCardsHasNext] = useState(false);
    const [cardsHasPrev, setCardsHasPrev] = useState(false);
    const [listTotals, setListTotals] = useState({ total_cards: 0, total_value: 0 });
    const [shouldFetchCards, setShouldFetchCards] = useState(false);

    const changeViewMode = mode => {
        localStorage.setItem('crm_view_mode', mode);
        setViewMode(mode);
    };

    // Filtros atualmente aplicados aos stages (usados também no scroll infinito).
    // Inicializa a partir da URL para que voltar de um card (botão ou back do
    // navegador) reaplique os filtros que estavam ativos.
    const [stagesFilterParams, setStagesFilterParams] = useState(() =>
        buildParamsFromSearchParams(new URLSearchParams(window.location.search))
    );

    const [filter, setFilter] = useState({
        activity_status: null,
        company_id: null,
        customer_id: null,
        admin_id: null,
        budget_code: '',
        crm_tag_ids: [],
        status: 'open',
        created_at_start: '',
        created_at_final: '',
        closing_date_start: '',
        closing_date_final: '',
        amount_start: '',
        amount_final: '',
        order_by: 'created_at,desc'
    });

    const [crmTags, setCrmTags] = useState([]);

    useEffect(() => {
        api.get('/crm-tag').then(res => {
            setCrmTags(res.data.resources ?? []);
        }).catch(() => {});
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilterCount = useMemo(() => countActiveCrmFilters(searchParams), [searchParams]);
    const navigate = useNavigate();
    const { id: routePipelineId } = useParams();

    // Estado da pipeline salvo ao abrir um card (paginação por etapa + scroll no
    // kanban; página + scroll na lista). É consumido uma única vez na montagem
    // para restaurar a pipeline exatamente como estava — tanto pelo botão
    // "Voltar para a pipeline" do card quanto pelo back manual do navegador.
    const pendingKanbanRestoreRef = useRef(null);
    const pendingListRestoreRef = useRef(null);

    const saveBoardState = () => {
        if (!pipeline?.id) return;

        const state = { stages: {} };
        stages.forEach(stage => {
            if (stage.cards_pagination) {
                state.stages[stage.id] = {
                    current_page: stage.cards_pagination.current_page || 1,
                    total_cards: stage.cards.length,
                    card_ids: stage.cards.map(c => c.id)
                };
            }
        });

        if (viewMode === 'kanban') {
            const columnsEl = document.querySelector('#crm .columns');
            const columns = {};
            document.querySelectorAll('#crm .columns .column-cards').forEach((el, index) => {
                const stageId = stages[index]?.id;
                if (stageId != null && el.scrollTop > 0) {
                    columns[stageId] = el.scrollTop;
                }
            });
            state.scroll = { columnsLeft: columnsEl?.scrollLeft || 0, columns };
        } else {
            const listBodyEl = document.querySelector('#crm > .box.with-pagination > .body');
            state.list = { page: cardsPage, scrollTop: listBodyEl?.scrollTop || 0 };
        }

        sessionStorage.setItem(boardStateKey(pipeline.id), JSON.stringify(state));
    };

    const readBoardState = pipelineId => {
        const saved = sessionStorage.getItem(boardStateKey(pipelineId));
        return saved ? JSON.parse(saved) : null;
    };

    useEffect(() => {
        const params = Object.fromEntries(searchParams.entries());
        const cleanParams = {};
        knownFilterKeys.forEach(key => {
            if (params[key] != null && params[key] !== '') {
                cleanParams[key] = params[key];
            }
        });
        // crm_tag_ids é multi-valor na URL (chave repetida), então usa getAll.
        cleanParams.crm_tag_ids = searchParams.getAll('crm_tag_ids');
        const orderByRaw = cleanParams.order_by || 'created_at,desc';
        const orderByParts = orderByRaw.split(',').filter(Boolean);
        const orderByNormalized = orderByParts.length >= 2
            ? `${orderByParts[0]},${orderByParts[orderByParts.length - 1]}`
            : 'created_at,desc';

        setFilter(prev => ({
            ...prev,
            ...cleanParams,
            order_by: orderByNormalized,
            amount_start: cleanParams.amount_start ? convertIntToMoney(cleanParams.amount_start) : '',
            amount_final: cleanParams.amount_final ? convertIntToMoney(cleanParams.amount_final) : '',
        }));
    }, [searchParams]);


    
    

    const [totals, setTotals] = useState({
        totalPipelineCards: 0,
        totalStages: []
    });

    const [pipelineTotals, setPipelineTotals] = useState([]);
    const [pipelineStageTotals, setPipelineStageTotals] = useState({ total_cards: 0, total_value: 0 });
    const [showMembersModal, setShowMembersModal] = useState(false);




    useEffect(() => {
        getPipelines();
    }, []);

    // Seleciona a pipeline a partir do parâmetro da rota (/crm/pipeline/:id).
    // A criação/edição da pipeline vive em página própria (/crm/pipeline/nova e
    // /crm/pipeline/:id/editar), não mais em modal aqui.
    useEffect(() => {
        if (gettingPipelines) return;
        const fromRoute = pipelines.find(p => String(p.id) === String(routePipelineId));
        if (fromRoute) {
            if (fromRoute.id !== pipeline?.id) setPipeline(fromRoute);
        } else {
            // id inexistente: volta para a lista de pipelines
            navigate('/crm', { replace: true });
        }
    }, [pipelines, routePipelineId, gettingPipelines]);

    useEffect(() => {
        if (pipeline?.id) {
            const keys = Object.keys(sessionStorage);
            keys.forEach(key => {
                if (key.startsWith('crm_board_state_') && key !== boardStateKey(pipeline.id)) {
                    sessionStorage.removeItem(key);
                }
                // chave legada, de antes do estado unificado da pipeline
                if (key.startsWith('crm_stages_pagination_')) {
                    sessionStorage.removeItem(key);
                }
            });

            // Estado salvo ao abrir um card: consome uma única vez para
            // restaurar paginação e scroll ao voltar.
            const saved = readBoardState(pipeline.id);
            if (saved) {
                sessionStorage.removeItem(boardStateKey(pipeline.id));
                pendingKanbanRestoreRef.current = saved;
                pendingListRestoreRef.current = saved.list ?? null;
            }

            fetchStages(pipeline.id, buildParamsFromSearchParams(searchParams), {
                restorePagination: viewMode === 'kanban' && !!saved
            });
        }
    }, [pipeline]);

    useEffect(() => {
        getTotals();
    }, [stages])

    // Restaura o scroll do kanban (board e colunas) salvo ao abrir um card.
    useEffect(() => {
        if (viewMode !== 'kanban' || gettingStages || stages.length === 0) return;
        const scroll = pendingKanbanRestoreRef.current?.scroll;
        pendingKanbanRestoreRef.current = null;
        if (!scroll) return;

        requestAnimationFrame(() => {
            const columnsEl = document.querySelector('#crm .columns');
            if (columnsEl) columnsEl.scrollLeft = scroll.columnsLeft || 0;
            document.querySelectorAll('#crm .columns .column-cards').forEach((el, index) => {
                const stageId = stages[index]?.id;
                if (stageId != null && scroll.columns?.[stageId]) {
                    el.scrollTop = scroll.columns[stageId];
                }
            });
        });
    }, [viewMode, gettingStages, stages]);

    // Restaura o scroll da visão em lista depois que a página salva carregar.
    useEffect(() => {
        if (viewMode !== 'list' || cardsLoading || cards.length === 0) return;
        const scrollTop = pendingListRestoreRef.current?.scrollTop;
        pendingListRestoreRef.current = null;
        if (!scrollTop) return;

        requestAnimationFrame(() => {
            const listBodyEl = document.querySelector('#crm > .box.with-pagination > .body');
            if (listBodyEl) listBodyEl.scrollTop = scrollTop;
        });
    }, [viewMode, cardsLoading, cards]);

    // Lista: ao trocar de modo/pipeline ou aplicar filtros, volta para a página 1 e busca
    useEffect(() => {
        if (viewMode !== 'list' || !pipeline?.id) return;

        // Voltando de um card: retoma a página em que a lista estava.
        const pendingList = pendingListRestoreRef.current;
        if (pendingList?.page > 1 && cardsPage !== pendingList.page) {
            pendingListRestoreRef.current = { ...pendingList, page: null };
            setCardsPage(pendingList.page); // dispara o efeito de página abaixo
            return;
        }

        if (cardsPage !== 1) {
            setCardsPage(1); // dispara o efeito de página abaixo
        } else {
            setShouldFetchCards(true);
        }
    }, [viewMode, pipeline?.id, stagesFilterParams]);

    // Lista: busca ao mudar de página
    useEffect(() => {
        if (viewMode === 'list' && pipeline?.id) {
            fetchCards(pipeline.id, stagesFilterParams, cardsPage);
        }
    }, [cardsPage]);

    useEffect(() => {
        if (shouldFetchCards) {
            if (viewMode === 'list' && pipeline?.id) {
                fetchCards(pipeline.id, stagesFilterParams, cardsPage);
            }
            setShouldFetchCards(false);
        }
    }, [shouldFetchCards]);

    const getTotals = () => {
        let totalPipelineCards = 0;
        const totalStages = stages.map(stage => {
            const totalCards = stage.cards.length;
            totalPipelineCards += totalCards;
            return {
                stageId: stage.id,
                totalCards: totalCards
            };
        });
        setTotals({
            totalPipelineCards,
            totalStages
        });
    }

    const getPipelineTotals = (pipelineId) => {
        const params = pipelineId ? { pipeline_id: pipelineId } : {};
        api.get(`/pipeline/totals`, { params }).then(res => {
            setPipelineTotals(res.data.pipelines ?? []);
        }).catch(error => {
            renderError(error);
        });
    };

    const getPipelines = () => {
        setGettingPipelines(true);

        api.get(`/pipeline`).then(res => {
            let toPipelines = res.data.resources;
            setPipelines(toPipelines);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingPipelines(false));
    }

    const fetchStages = async (pipelineId, params = {}, { silent = false, restorePagination = false } = {}) => {
        if (pipelineId == null || String(pipelineId).trim() === '') {
            if (!silent) {
                setStages([]);
                setPipelineStageTotals({ total_cards: 0, total_value: 0 });
                setGettingStages(false);
            }
            return Promise.resolve();
        }

        if (!silent) {
            setStages([]);
            setPipelineStageTotals({ total_cards: 0, total_value: 0 });
            setGettingStages(true);
        }

        const requestParams = {
            ...params,
            per_page: cardsPerPage
        };

        try {
            const res = await api.get(`/pipeline-stage?pipeline_id=${pipelineId}`, { params: requestParams });
            let initialStages = res.data.stages ?? [];
            
             if (restorePagination) {
                const savedState = pendingKanbanRestoreRef.current?.stages;
                if (savedState) {
                    const stagesWithPagination = await Promise.all(
                        initialStages.map(async (stage) => {
                            const saved = savedState[stage.id];
                            if (saved && saved.current_page > 1) {
                                
                                let allCards = [...(stage.cards || [])];
                                let currentPage = 1;
                                
                                while (currentPage < saved.current_page) {
                                    currentPage++;
                                    try {
                                        const cardParams = {
                                            ...stagesFilterParams,
                                            pipeline_stage_id: stage.id,
                                            page: currentPage,
                                            per_page: cardsPerPage
                                        };
                                        
                                        const cardRes = await api.get(`/pipeline-stage/cards`, { params: cardParams });
                                        const newCards = cardRes.data.cards ?? cardRes.data.resources ?? [];
                                        allCards = [...allCards, ...newCards];
                                        
                                        if (cardRes.data.cards_pagination) {
                                            stage.cards_pagination = cardRes.data.cards_pagination;
                                        }
                                    } catch (error) {
                                        console.error(`Erro ao carregar página ${currentPage} do stage ${stage.id}:`, error);
                                        break;
                                    }
                                }
                                
                                return {
                                    ...stage,
                                    cards: allCards,
                                    cards_pagination: {
                                        ...stage.cards_pagination,
                                        current_page: saved.current_page
                                    }
                                };
                            }
                            return stage;
                        })
                    );
                    
                    initialStages = stagesWithPagination;
                }
            }
            
            
            setStages(initialStages);
            setPipelineStageTotals({
                total_cards: res.data.total_cards ?? 0,
                total_value: res.data.total_value ?? 0
            });
            if (!silent) {
                const { page, per_page, ...filtersOnly } = requestParams;
                // Mantém a identidade quando o conteúdo não mudou, para não
                // redisparar os efeitos que dependem de stagesFilterParams.
                setStagesFilterParams(prev =>
                    JSON.stringify(prev) === JSON.stringify(filtersOnly) ? prev : filtersOnly
                );
            }
        } catch (error) {
            renderError(error);
        } finally {
            if (!silent) setGettingStages(false);
        }
    };

    const getStages = (pipelineId) => {
        if (pipelineId == null || String(pipelineId).trim() === '') return;
        fetchStages(pipelineId, stagesFilterParams);
    };

    const loadMoreCards = async (stageId) => {
        const stageIndex = stages.findIndex(stage => stage.id === stageId);
        if (stageIndex === -1) {
            return;
        }

        const stage = stages[stageIndex];
        const pagination = stage.cards_pagination;

        if (!pagination || pagination.current_page >= pagination.last_page) {
            return;
        }

        const nextPage = pagination.current_page + 1;

        try {
            const params = {
                ...stagesFilterParams,
                pipeline_stage_id: stageId,
                page: nextPage,
                per_page: cardsPerPage
            };

            const res = await api.get(`/pipeline-stage/cards`, { params });

            let newCards = res.data.cards ?? res.data.resources ?? [];
            const existingIds = new Set(stage.cards.map(c => c.id));
            newCards = newCards.filter(c => !existingIds.has(c.id));

            // Se o backend não devolver uma nova paginação, avançamos manualmente a página atual
            let newPagination = res.data.cards_pagination
                ? res.data.cards_pagination
                : { ...pagination, current_page: nextPage };

            // Se por algum motivo vier vazio, consideramos que chegamos ao fim
            if (newCards.length === 0) {
                newPagination = {
                    ...newPagination,
                    current_page: newPagination.last_page ?? newPagination.current_page
                };
            }

            const updatedStages = [...stages];
            updatedStages[stageIndex] = {
                ...stage,
                cards: [...stage.cards, ...newCards],
                cards_pagination: newPagination
            };

            setStages(updatedStages);
        } catch (error) {
            renderError(error);
        }
    };

    const refillStageAfterCardRemoval = async (stagesState, oldStageIndex) => {
        const stage = stagesState[oldStageIndex];
        if (!stage?.cards_pagination) return stagesState;

        const { cards, cards_pagination } = stage;
        const cardsNeeded = cardsPerPage - cards.length;

        if (
            cardsNeeded <= 0 ||
            !cards_pagination.last_page ||
            cards_pagination.current_page >= cards_pagination.last_page
        ) {
            return stagesState;
        }

        const nextPage = cards_pagination.current_page + 1;

        try {
            const params = {
                ...stagesFilterParams,
                pipeline_stage_id: stage.id,
                page: nextPage,
                per_page: Math.max(5, Math.min(cardsNeeded, cardsPerPage))
            };

            const res = await api.get(`/pipeline-stage/cards`, { params });
            const newCards = res.data.cards ?? res.data.resources ?? [];
            const existingIds = new Set(cards.map(c => c.id));
            const uniqueNewCards = newCards.filter(c => !existingIds.has(c.id));
            const cardsToAdd = uniqueNewCards.slice(0, cardsNeeded);

            if (cardsToAdd.length === 0) return stagesState;

            const updatedStages = [...stagesState];
            updatedStages[oldStageIndex] = {
                ...stage,
                cards: [...cards, ...cardsToAdd],
                cards_pagination: {
                    ...(res.data.cards_pagination || cards_pagination),
                    current_page: cards_pagination.current_page
                }
            };

            return updatedStages;
        } catch (error) {
            renderError(error);
            return stagesState;
        }
    };

    const moveCard = (cardId, oldStageId, newStageId) => {
        if (oldStageId === newStageId) return;
        const oldStageIndex = stages.findIndex(stage => stage.id === oldStageId);
        if (oldStageIndex === -1) return;
        const card = stages[oldStageIndex].cards.find(c => c.id === cardId);
        if (!card) return;
        updateCard({ ...card, pipeline_stage_id: newStageId });
    };

    const updateCard = async card => {
        const oldStages = JSON.parse(JSON.stringify(stages));

        let newStageIndex = null;
        let currentStage = null;
        let currentStageIndex = null;
        let currentCardIndex = null;
        let newCardIndex = null;
        let updatedStages = stages;

        for (let indexMapStage = 0; indexMapStage < stages.length; indexMapStage++) {
            const mapStage = stages[indexMapStage];
            if (mapStage.id === card.pipeline_stage_id) newStageIndex = indexMapStage;
            for (let indexMapCard = 0; indexMapCard < mapStage.cards.length; indexMapCard++) {
                if (mapStage.cards[indexMapCard].id === card.id) {
                    currentStage = mapStage;
                    currentStageIndex = indexMapStage;
                    currentCardIndex = indexMapCard;
                    break;
                }
            }
        }

        if (!currentStage || newStageIndex == null) {
            renderError(new Error('Card ou etapa não encontrados'));
            return false;
        }

        if (currentStage.id !== card.pipeline_stage_id) {
            updatedStages[currentStageIndex].cards.splice(currentCardIndex, 1);
            updatedStages[newStageIndex].cards.unshift({...card, updating: true});
            newCardIndex = 0;
        } else {
            newCardIndex = currentCardIndex;
            updatedStages[newStageIndex].cards[newCardIndex] = {...card, updating: true};
        }

        updatedStages = JSON.parse(JSON.stringify(updatedStages));
        setStages( updatedStages );

        const isMovingStage = currentStage.id !== card.pipeline_stage_id;
        const oldStagePag = currentStage.cards_pagination;
        const newStagePag = updatedStages[newStageIndex].cards_pagination;
        const cardAmount = Number(card.amount) || 0;

        let res;
        try {
            res = await api.put(`/pipeline-stage-card/${card.id}`, {
                temperature: card.temperature,
                amount: card.amount,
                customer_id: card.customer_id,
                company_id: card.company_id,
                pipeline_stage_id: card.pipeline_stage_id,
                admin_id: card.admin_id,
                status: card.status,
                origin: card.origin,
                closing_date: card.closing_date,
            });

            updatedStages[newStageIndex].cards[newCardIndex] = {...res.data.resource, updating: false};
            updatedStages = JSON.parse(JSON.stringify(updatedStages));

            const newAmount = Number(res.data.resource?.amount) || 0;

            const valueInSameUnit = (val) => {
                const t = Number(oldStagePag?.total_value);
                return Number.isInteger(t) ? val : val / 100;
            };

            if (isMovingStage) {
                const oldTotal = (oldStagePag?.total ?? currentStage.cards.length) - 1;
                const oldValue = (Number(oldStagePag?.total_value) ?? 0) - valueInSameUnit(cardAmount);
                const newTotal = (newStagePag?.total ?? updatedStages[newStageIndex].cards.length) + 1;
                const newValue = (Number(newStagePag?.total_value) ?? 0) + valueInSameUnit(newAmount);

                if (updatedStages[currentStageIndex].cards_pagination) {
                    updatedStages[currentStageIndex].cards_pagination = {
                        ...updatedStages[currentStageIndex].cards_pagination,
                        total: Math.max(0, oldTotal),
                        total_value: Math.max(0, oldValue)
                    };
                }
                if (updatedStages[newStageIndex].cards_pagination) {
                    updatedStages[newStageIndex].cards_pagination = {
                        ...updatedStages[newStageIndex].cards_pagination,
                        total: newTotal,
                        total_value: newValue
                    };
                }

                const refilledStages = await refillStageAfterCardRemoval(updatedStages, currentStageIndex);
                if (refilledStages !== updatedStages) {
                    updatedStages = refilledStages;
                }
            } else {
                const oldCardAmount = Number(currentStage.cards[currentCardIndex]?.amount) || 0;
                const diff = valueInSameUnit(newAmount) - valueInSameUnit(oldCardAmount);
                if (diff !== 0 && updatedStages[newStageIndex].cards_pagination?.total_value != null) {
                    updatedStages[newStageIndex].cards_pagination = {
                        ...updatedStages[newStageIndex].cards_pagination,
                        total_value: Math.max(0, Number(updatedStages[newStageIndex].cards_pagination.total_value) + diff)
                    };
                }
            }

            setStages(updatedStages);
            res = true;
        } catch (error) {
            setStages(oldStages);
            renderError(error);
            res = false;
        }

        return res;
    } 



    const findCard = (stage, cardId) => {
        try {
            const card = stage?.cards?.find(c => c.id === cardId);
            return {
                card,
                index: card != null && stage?.cards ? stage.cards.indexOf(card) : -1
            };
        } catch (error) {
            return { card: undefined, index: -1 };
        }
    };

    const handleChangePipeline = option => {
        navigate(`/crm/pipeline/${option.value}`);
    }

    const createPipeline = () => navigate('/crm/pipeline/nova');

    const createCard = () => {
        setCardToCreate({
            temperature: "warm",
            customer_id: null,
            company_id: null,
            pipeline_stage_id: null,
            status: "open",
            origin: "sistema",
            amount_formatted: "R$ 0,00",
            closing_date: null,
            link_type: "standalone",
            budget_id: null,
            order_id: null
        });
    }

    // Trocar o vínculo limpa o registro selecionado e os campos que passam a
    // ser preenchidos automaticamente pelo orçamento/pedido.
    const changeCardLinkType = value => {
        setCardToCreate(prev => ({
            ...prev,
            link_type: value,
            budget_id: null,
            order_id: null,
            ...(value !== 'standalone' ? {
                customer_id: null,
                company_id: null,
                amount_formatted: "R$ 0,00"
            } : {})
        }));
    }

    const updateFromStoredCompany = company => {
        setCardToCreate(prev => ({
            ...prev,
            company_id: company.id
        }));
        setShowCreateCompany(false);
    }

    const updateFromStoredCustomer = customer => {
        setCardToCreate(prev => ({
            ...prev,
            customer_id: customer.id
        }));
        setShowCreateCustomer(false);
    }

    const storeCard = () => {
        if (cardToCreate.link_type === 'budget' && !cardToCreate.budget_id) {
            toast.error('Selecione o orçamento que será vinculado ao card');
            return;
        }
        if (cardToCreate.link_type === 'order' && !cardToCreate.order_id) {
            toast.error('Selecione o pedido que será vinculado ao card');
            return;
        }

        const { link_type, ...cardData } = cardToCreate;

        setCreatingCard(true);
        api.post(`/pipeline-stage-card`, {
            ...cardData,
            budget_id: link_type === 'budget' ? cardToCreate.budget_id : null,
            order_id: link_type === 'order' ? cardToCreate.order_id : null,
            amount: convertMoenyToInt(cardToCreate.amount_formatted),
            closing_date: cardToCreate.closing_date ? moment(cardToCreate.closing_date).format('YYYY-MM-DD') : null
        }).then(res => {
            toast.success('Card criado com sucesso');
            setCardToCreate(null);
            if (pipeline?.id != null && String(pipeline.id).trim() !== '') {
                getStages(pipeline.id);
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setCreatingCard(false));
    }

    const handleFilterChange = (param, value) => {
        setFilter(prev => ({
            ...prev,
            [param]: value
        }));
    }

    // handleClear
    const handleClear = () => {
        setFilter({
            activity_status: null,
            company_id: null,
            customer_id: null,
            admin_id: null,
            budget_code: '',
            crm_tag_ids: [],
            status: 'open',
            created_at_start: '',
            created_at_final: '',
            closing_date_start: '',
            closing_date_final: '',
            amount_start: '',
            amount_final: '',
            order_by: 'created_at,desc'
        });

        toast.success('Filtros limpos com sucesso!');

        handleFilter(true);
    };

    const buildFilterParams = (isClearing = false) => {
        if (isClearing) {
            return { status: 'open', order_by: 'created_at,desc' };
        }

        const orderByRaw = filter.order_by || 'created_at,desc';
        const orderByParts = orderByRaw.split(',').filter(Boolean);
        const orderField = orderByParts[0] || 'created_at';
        const orderDirection = orderByParts[orderByParts.length - 1] === 'asc' ? 'asc' : 'desc';

        const amountStart = filter.amount_start ? convertMoenyToInt(filter.amount_start) : null;
        const amountFinal = filter.amount_final ? convertMoenyToInt(filter.amount_final) : null;

        const params = {
            activity_status: filter.activity_status ?? null,
            company_id: filter.company_id ?? null,
            customer_id: filter.customer_id ?? null,
            admin_id: filter.admin_id ?? null,
            budget_code: filter.budget_code ?? '',
            crm_tag_ids: (filter.crm_tag_ids ?? []).length > 0 ? filter.crm_tag_ids : null,
            status: filter.status ?? 'open',
            created_at_start: filter.created_at_start ?? '',
            created_at_final: filter.created_at_final ?? '',
            closing_date_start: filter.closing_date_start ?? '',
            closing_date_final: filter.closing_date_final ?? '',
            amount_start: amountStart,
            amount_final: amountFinal,
            order_by: `${orderField},${orderDirection}`
        };

        Object.keys(params).forEach(key => {
            if (params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        return params;
    };

    const fetchCards = async (pipelineId, params = {}, page = 1) => {
        if (pipelineId == null || String(pipelineId).trim() === '') {
            setCards([]);
            setCardsTotal(0);
            setCardsLastPage(1);
            setCardsHasNext(false);
            setCardsHasPrev(false);
            setListTotals({ total_cards: 0, total_value: 0 });
            return;
        }

        setCardsLoading(true);

        try {
            const res = await api.get(`/pipeline-stage/cards-list`, {
                params: { pipeline_id: pipelineId, ...params, page, per_page: cardsPerPage }
            });

            const pagination = res.data.pagination ?? {};
            const currentPage = pagination.current_page ?? page;
            const lastPage = pagination.last_page ?? 1;

            setCards(res.data.cards ?? []);
            setCardsLastPage(lastPage);
            setCardsTotal(pagination.total ?? 0);
            setCardsHasNext(currentPage < lastPage);
            setCardsHasPrev(currentPage > 1);
            setListTotals({
                total_cards: res.data.total_cards ?? 0,
                total_value: res.data.total_value ?? 0
            });
        } catch (error) {
            renderError(error);
        } finally {
            setCardsLoading(false);
        }
    };

    const handleFilter = async (isClearing = false) => {
        try {
            if (pipeline?.id == null || String(pipeline.id).trim() === '') {
                toast.error('Selecione uma pipeline válida antes de aplicar filtros.');
                return;
            }

            if (pipeline?.id) {
                sessionStorage.removeItem(boardStateKey(pipeline.id));
            }

            const params = buildFilterParams(isClearing);

            setSearchParams(params, { replace: true });

            if (viewMode === 'list') {
                // dispara a busca da lista via efeito (depende de stagesFilterParams)
                setStagesFilterParams(params);
            } else {
                setGettingStages(true);
                await fetchStages(pipeline.id, params);
            }

            setEditFilter(false);
            toast.success('Filtros aplicados com sucesso!');
        } catch (error) {
            renderError(error);
            toast.error('Erro ao aplicar filtros.');
        }
    };

    // Soma bruta dos valores das etapas (na unidade em que o backend devolveu),
    // usada só para a participação percentual de cada coluna.
    const boardValue = stages.reduce((sum, s) => sum + (Number(s.cards_pagination?.total_value) || 0), 0);

    const boardSummary = (() => {
        let totalCards;
        let totalValue;
        if (viewMode === 'list') {
            totalCards = listTotals.total_cards;
            totalValue = listTotals.total_value;
        } else if (stages.length > 0) {
            totalCards = stages.reduce((sum, s) => sum + (Number(s.cards_pagination?.total) || 0), 0);
            totalValue = boardValue;
        } else {
            totalCards = pipelineStageTotals.total_cards;
            totalValue = pipelineStageTotals.total_value;
        }
        return {
            totalCards: Number(totalCards) || 0,
            totalCents: totalValue != null
                ? (Number.isInteger(totalValue) ? totalValue : Math.round(Number(totalValue) * 100))
                : null
        };
    })();

    return (
        <PanelTemplate id="crm">
            <CardDragLayer />
            <ModalCreateCustomer
                show={showCreateCustomer}
                close={() => setShowCreateCustomer(false)}
                customerStored={updateFromStoredCustomer}
                showSuccess={false}
                type={['customer']}
                companies={cardToCreate?.company_id ? [cardToCreate.company_id] : null}
                hideCompany={true}
            />
            <ModalCreateCompany
                show={showCreateCompany}
                close={() => setShowCreateCompany(false)}
                companyStored={updateFromStoredCompany}
                showSuccess={false}
                type={['customer']}
            />
            {editFilter &&
                <Modal
                    show={true}o
                    close={() => setEditFilter(false)}
                    title={`Filtrar e ordenar`}
                    size={`big`}
                    footer={
                        <>
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Limpar filtros`}
                                action={handleClear}
                            />
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Filtrar resultados`}
                                action={handleFilter}
                                loading={gettingPipelines || gettingStages}
                            />
                            
                        </>
                    }
                >
                    <div className="section">
                        <h2 className="section-title" style={{ marginBottom: 15}}>Ordenar</h2>
                        <div className="row">
                            <div className="col-6">
                                <InputSelect label={`Ordernar por`} options={orderByOptions} value={filter?.order_by || 'created_at,desc'} change={value => handleFilterChange('order_by', value)} />
                            </div>
                            {/* <div className="col-4">
                                <InputSelect label={`Ordem`} options={orderDirOptions} value={filter?.order_dir} change={value => handleFilterChange('order_dir', value)} />
                            </div> */}
                        </div>

                        <h2 className="section-title" style={{ marginBottom: 15}}>Filtrar</h2>
                        <div className="row">
                            {/* <div className="col-4">
                                <InputSelect 
                                    label={`Origem`} 
                                    options={originOptions} 
                                    value={filter?.origin} 
                                    change={value => handleFilterChange('origin', value)} 
                                />
                            </div> */}
                            <div className="col-6">
                                <InputSelect 
                                    label={`Status das atividades`} 
                                    options={activityStatusOptions} 
                                    value={filter?.activity_status} 
                                    change={value => handleFilterChange('activity_status', value)} 
                                />
                            </div>
                            <div className="col-6">
                                <Input 
                                    label={`Núm. do orçamento`} 
                                    value={filter?.budget_code} 
                                    change={value => handleFilterChange('budget_code', value)}
                                />
                            </div>
                            {/* <div className="col-4">
                                <InputSelect 
                                    label={`Status dos e-mails`} 
                                    options={emailStatusOptions} 
                                    value={filter?.email_status} 
                                    change={value => handleFilterChange('email_status', value)}
                                />
                            </div> */}
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <SelectCompany
                                    selectedCompany={filter.company_id}
                                    setSelectedCompany={value => handleFilterChange('company_id', value)}
                                />
                            </div>
                            <div className="col-6">
                                <SelectCustomer
                                    selectedCustomer={filter.customer_id}
                                    setSelectedCustomer={value => handleFilterChange('customer_id', value)}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <InputSelect
                                    label={`Etiquetas`}
                                    isMulti
                                    options={createOptions(crmTags, 'name', 'id')}
                                    value={filter.crm_tag_ids}
                                    change={value => handleFilterChange('crm_tag_ids', value)}
                                    noOptionsText={`Nenhuma etiqueta cadastrada`}
                                />
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    label={`Status do card`}
                                    options={[
                                        { label: 'Aberto', value: 'open' },
                                        { label: 'Ganho', value: 'won' },
                                        { label: 'Perdido', value: 'lost' },
                                        { label: 'Arquivado', value: 'archived' },
                                        { label: 'Todos', value: 'all' },
                                    ]}
                                    value={filter.status}
                                    change={value => handleFilterChange('status', value)}
                                />
                            </div>
                        </div>
                        {!isRestrictedCardView() && (
                            <div className="row">
                                <div className="col-6">
                                    <SelectAdmin
                                        selectedAdmin={filter.admin_id}
                                        setSelectedAdmin={value => handleFilterChange('admin_id', value)}
                                        nullable={true}
                                        labelNullable="Sem responsável"
                                        nullableValue="unassigned"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="row">
                            {/* <div className="col-4">
                                <InputSelect 
                                    label={`Segmento`} 
                                    options={segmentsOptions} 
                                    value={filter?.segment} 
                                    change={value => handleFilterChange('segment', value)}
                                />
                            </div> */}
                            {/* <div className="col-4">
                                <InputSelect 
                                    label={`Local da entrega`} 
                                    options={states} 
                                    value={filter?.segment} 
                                    change={value => handleFilterChange('segment', value)}
                                />
                            </div> */}
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <label className="filter-group-label">Data da criação do card</label>
                                <div className="row">
                                    <div className="col-6">
                                        <InputDate
                                            label={`A partir de`}
                                            format={`YYYY-MM-DD`} 
                                            value={filter.created_at_start}
                                            change={value => handleFilterChange('created_at_start', value)}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <InputDate
                                            label={`Até`}
                                            format={`YYYY-MM-DD`} 
                                            value={filter.created_at_final}
                                            change={value => handleFilterChange('created_at_final', value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-6">
                                <label className="filter-group-label">Previsão de conclusão</label>
                                <div className="row">
                                    <div className="col-6">
                                        <InputDate
                                            label={`A partir de`}
                                            format={`YYYY-MM-DD`} 
                                            value={filter.closing_date_start}
                                            change={value => handleFilterChange('closing_date_start', value)}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <InputDate
                                            label={`Até`}
                                            format={`YYYY-MM-DD`} 
                                            value={filter.closing_date_final}
                                            change={value => handleFilterChange('closing_date_final', value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <label className="filter-group-label">Valor total do card</label>
                                <div className="row">
                                    <div className="col-6">
                                        <Input
                                            label={`A partir de`}
                                            value={filter.amount_start} 
                                            change={value => handleFilterChange('amount_start', value)}
                                            mask={`convertToMoney`}
                                        />
                                    </div>
                                    {/* <div className="col-1 filter-group-divisor">até</div> */}
                                    <div className="col-6">
                                        <Input 
                                            label={`Até`}
                                            value={filter.amount_final} 
                                            change={value => handleFilterChange('amount_final', value)}
                                            mask={`convertToMoney`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            }
            {cardToCreate !== null &&
                <Modal
                    show={!showCreateCompany && !showCreateCustomer}
                    close={() => setCardToCreate(null)}
                    title={`Criar card`}
                    size={`small`}
                    footer={<Button
                        type={`primary`}
                        size={`small`}
                        text={`Salvar alterações`}
                        svg={`save-white`}
                        action={storeCard}
                        loading={creatingCard} 
                    />}
                >
                    <div className="section">
                        <InputSelect
                            label={`Vínculo do card *`}
                            options={cardLinkTypeOptions}
                            value={cardToCreate?.link_type ?? 'standalone'}
                            change={changeCardLinkType}
                        />

                        {cardToCreate.link_type === 'budget' &&
                            <>
                                <SelectBudget
                                    label={`Orçamento *`}
                                    selectedBudget={cardToCreate.budget_id}
                                    setSelectedBudget={value => {
                                        setCardToCreate(prev => ({
                                            ...prev,
                                            budget_id: value
                                        }));
                                    }}
                                />
                                <p className="crm-card-link-hint">
                                    Valor, empresa, pessoa e responsável serão preenchidos automaticamente com os dados do orçamento selecionado.
                                </p>
                            </>
                        }

                        {cardToCreate.link_type === 'order' &&
                            <>
                                <SelectOrder
                                    label={`Pedido *`}
                                    selectedOrder={cardToCreate.order_id}
                                    setSelectedOrder={value => {
                                        setCardToCreate(prev => ({
                                            ...prev,
                                            order_id: value
                                        }));
                                    }}
                                />
                                <p className="crm-card-link-hint">
                                    Valor, empresa, pessoa e responsável serão preenchidos automaticamente com os dados do pedido selecionado.
                                </p>
                            </>
                        }

                        <InputSelect
                            label={`Etapa da pipeline *`}
                            options={createOptions(pipeline.stages, 'name', 'id')}
                            value={cardToCreate?.pipeline_stage_id ?? null}
                            change={value => {
                                setCardToCreate(prev => ({
                                    ...prev,
                                    pipeline_stage_id: value
                                }));
                            }}
                        />

                        {/* <InputSelect
                            label={`Temperatura *`}
                            options={temperatureOptions}
                            value={cardToCreate?.temperature ?? null}
                            change={value => {
                                setCardToCreate(prev => ({
                                    ...prev,
                                    temperature: value
                                }));
                            }}
                        /> */}

                        {cardToCreate.link_type === 'standalone' &&
                            <Input
                                label={`Valor`}
                                value={cardToCreate?.amount_formatted ?? null}
                                change={value => {
                                    setCardToCreate(prev => ({
                                        ...prev,
                                        amount_formatted: convertToMoney(value)
                                    }));
                                }}
                            />
                        }

                        <InputDate
                            value={cardToCreate?.closing_date ?? null}
                            change={value => {
                                setCardToCreate(prev => ({
                                    ...prev,
                                    closing_date: value
                                }));
                            }}
                            label={`Previsão de conclusão`}
                        />

                        {cardToCreate.link_type === 'standalone' &&
                            <>
                                <div className="row">
                                    <div className="col">
                                        <SelectCustomerCompany
                                            selectedCustomerCompany={cardToCreate.company_id}
                                            setSelectedCustomerCompany={value => {
                                                setCardToCreate(prev => ({
                                                    ...prev,
                                                    company_id: value
                                                }));
                                            }}
                                        />
                                        <a
                                            href="#"
                                            className="link-pos-input"
                                            onClick={e => {
                                                e.preventDefault();
                                                setShowCreateCompany(true);
                                            }}
                                        >
                                            Cadastrar nova empresa
                                        </a>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <SelectCustomer
                                            selectedCustomer={cardToCreate.customer_id}
                                            setSelectedCustomer={value => {
                                                setCardToCreate(prev => ({
                                                    ...prev,
                                                    customer_id: value
                                                }));
                                            }}
                                            companyId={cardToCreate?.companyId ?? null}
                                            relatedWithCompany={cardToCreate?.companyId ? true : false}
                                        />
                                        <a
                                            href="#"
                                            className="link-pos-input"
                                            onClick={e => {
                                                e.preventDefault();
                                                setShowCreateCustomer(true);
                                            }}
                                        >
                                            Cadastrar nova pessoa
                                        </a>
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                </Modal>
            }
            <header className="crm-topbar">
                <div className="crm-topbar__identity">
                    <span className="crm-topbar__emoji" aria-hidden="true">
                        {pipeline?.icon || <Icon name="crm" size={20} />}
                    </span>
                    <div className="crm-topbar__titles">
                        <span className="crm-topbar__eyebrow">Pipeline</span>
                        <Select
                            className="crm-pipeline-switch"
                            classNamePrefix="crm-pipeline-switch"
                            options={createOptions(pipelines, 'name', 'id')}
                            value={pipeline ? {label: pipeline.name, value: pipeline.id} : null}
                            onChange={handleChangePipeline}
                            styles={pipelineSwitchStyles}
                            placeholder="Escolha uma pipeline"
                            noOptionsMessage={() => 'Nenhuma pipeline encontrada'}
                            isSearchable={pipelines.length > 6}
                            aria-label="Selecionar pipeline"
                        />
                        {pipeline &&
                            <div className="crm-topbar__stats">
                                <span><strong>{boardSummary.totalCards}</strong> card{boardSummary.totalCards === 1 ? '' : 's'}</span>
                                <span className="crm-topbar__dot" aria-hidden="true" />
                                <span><strong>{boardSummary.totalCents != null ? convertIntToMoney(boardSummary.totalCents, true) : '—'}</strong> em valor total</span>
                            </div>
                        }
                    </div>
                </div>
                <div className="crm-topbar__actions">
                    {pipeline &&
                        <div className="crm-topbar__group">
                            <div className="crm-view-switch" role="group" aria-label="Visualização dos cards">
                                <button
                                    type="button"
                                    className={viewMode === 'kanban' ? 'active' : ''}
                                    onClick={() => changeViewMode('kanban')}
                                    aria-pressed={viewMode === 'kanban'}
                                    title="Visualizar como Kanban"
                                >
                                    <Icon name="kanban-view" />
                                    <span>Kanban</span>
                                </button>
                                <button
                                    type="button"
                                    className={viewMode === 'list' ? 'active' : ''}
                                    onClick={() => changeViewMode('list')}
                                    aria-pressed={viewMode === 'list'}
                                    title="Visualizar como lista"
                                >
                                    <Icon name="list-view" />
                                    <span>Lista</span>
                                </button>
                            </div>
                            <button
                                type="button"
                                className="crm-members"
                                onClick={() => setShowMembersModal(true)}
                                title="Configurar usuários com acesso a esta pipeline"
                                aria-label="Configurar usuários com acesso a esta pipeline"
                            >
                                <PipelineMembersAvatars members={pipeline.members} size={24} showLabel={false} emptyLabel="" />
                                <span className="crm-members__label">
                                    {(pipeline.members ?? []).length > 0 ? 'Acesso' : 'Definir acesso'}
                                </span>
                            </button>
                        </div>
                    }
                    <div className="crm-topbar__group">
                        <ButtonDropdown
                            type={`secondary`}
                            size={`small`}
                            leadingSvg={`config`}
                            text={`Gerenciar`}
                            actions={[
                                ...(pipelines.length > 0 && pipeline?.id ? [{
                                    text: 'Editar pipeline',
                                    action: () => navigate(`/crm/pipeline/${pipeline.id}/editar`)
                                }] : []),
                                ...(hasPlanFeature('crm') && hasPermission('edit_crm_settings') ? [{
                                    text: 'Automações desta pipeline',
                                    link: `/crm/automacoes/pipeline/${pipeline?.id}`
                                }, {
                                    text: 'Todas as automações',
                                    link: '/crm/automacoes'
                                }] : []),
                                {
                                    text: 'Criar nova pipeline',
                                    action: createPipeline
                                }
                            ]}
                        />
                        <Button
                            type={`secondary`}
                            size={`small`}
                            svg={`filter`}
                            text={`Filtrar e ordenar`}
                            badge={activeFilterCount}
                            action={() => setEditFilter(true)}
                        />
                    </div>
                    {/* Pipelines "somente por automações" não expõem a criação manual de cards */}
                    {pipelines.length > 0 && pipeline?.allow_manual_card_creation !== false &&
                        <div className="crm-topbar__group crm-topbar__group--main">
                            <Button
                                type={`primary`}
                                size={`small`}
                                svg={`plus-white`}
                                text={`Novo card`}
                                action={createCard}
                            />
                        </div>
                    }
                </div>
            </header>

            {showMembersModal && pipeline &&
                <PipelineMembersModal
                    pipeline={pipeline}
                    close={saved => {
                        setShowMembersModal(false);
                        if (saved) getPipelines();
                    }}
                />
            }

                
            {!gettingPipelines && pipelines.length === 0 &&
                <Box>
                    <h1>Crie uma pipeline</h1>
                    <p>Você ainda não criou nenhuma pipeline, <a href="#" onClick={e => {e.preventDefault(); createPipeline();}}>crie uma pipeline</a> para começar a usar o CRM.</p>
                </Box>
            }
            {!gettingPipelines && pipelines.length > 0 && viewMode === 'kanban' &&
                <div className="columns">
                    {gettingStages && [0, 1, 2, 3].map(index => (
                        <div key={index} className="column column--skeleton" aria-hidden="true">
                            <div className="column-title">
                                <span className="skeleton skeleton--title" />
                                <span className="skeleton skeleton--meta" />
                                <span className="column-bar"><span style={{ width: '40%' }} /></span>
                            </div>
                            <div className="column-cards">
                                <span className="skeleton skeleton--card" />
                                <span className="skeleton skeleton--card" />
                                {index % 2 === 0 && <span className="skeleton skeleton--card" />}
                            </div>
                        </div>
                    ))}
                    {!gettingStages && stages.map((stage, index) => (
                        <Column
                            key={stage.id}
                            index={index}
                            stage={stage}
                            pipeline={pipeline}
                            totals={totals}
                            boardValue={boardValue}
                            moveCard={moveCard}
                            findCard={findCard}
                            updateCard={updateCard}
                            onLoadMore={loadMoreCards}
                            onCardClick={saveBoardState}
                        />
                    ))}
                </div>
            }
            {!gettingPipelines && pipelines.length > 0 && viewMode === 'list' &&
                <>
                    <Box className={`no-padding with-pagination`}>
                        <Table
                            header={['Código', 'Etapa', 'Tags', 'Empresa', 'Pessoa', 'Responsável', 'Valor', 'Dias na etapa', 'Próxima atividade', 'Status']}
                            rows={cards.map(card => ({
                                data: [
                                    card.order_item
                                        ? <CardListProduct orderItem={card.order_item} code={card.code} />
                                        : (card.code ?? '—'),
                                    card.stage_name ?? '—',
                                    (card.crm_tags ?? []).length > 0 ? <CardTags tags={card.crm_tags} /> : '—',
                                    card.company?.trade_name ?? '—',
                                    card.customer?.name ?? '—',
                                    card?.admin ? <UserCell name={card.admin.name} photo={card.admin.photo_url} /> : 'Sem responsável',
                                    convertIntToMoney(card.amount, true),
                                    `${card.days_in_stage} dia${card.days_in_stage !== 1 ? 's' : ''}`,
                                    taskActivityLabel(card),
                                    statusLabel[card.status] ?? card.status
                                ],
                                to: `/crm/card/${card.id}`,
                                state: { from: 'crm-board' },
                                beforeNavigate: saveBoardState,
                                id: null
                            }))}
                            loading={cardsLoading}
                        />
                    </Box>
                    {!cardsLoading &&
                        <Pagination
                            currentPage={cardsPage}
                            lastPage={cardsLastPage}
                            perPage={cardsPerPage}
                            totalResults={cardsTotal}
                            hasNextPage={cardsHasNext}
                            hasPrevPage={cardsHasPrev}
                            setCurrentPage={setCardsPage}
                        />
                    }
                </>
            }
        </PanelTemplate>
    );
}

export class SmartPointerSensor extends PointerSensor {
    static activators = [
        {
            eventName: "onPointerDown",
            handler: ({ nativeEvent: event }) => {
                if (
                    !event.isPrimary ||
                    event.button !== 0 ||
                    isInteractiveElement(event.target)
                ) {
                    return false;
                }

                return true;
            },
        },
    ];
}

function isInteractiveElement(element) {
    return !element.classList.contains('drag');
}


export default CRMList;
